/**
 * AgentOrb — a single visual signature that morphs between states.
 * States: idle | seeing | listening | thinking | executing
 */
import { cn } from "@/lib/utils";

export type AgentOrbState = "idle" | "seeing" | "listening" | "thinking" | "executing";

interface AgentOrbProps {
  state?: AgentOrbState;
  size?: number;
  className?: string;
}

const RING_COLORS: Record<AgentOrbState, string> = {
  idle: "rgba(87,204,153,0.35)",
  seeing: "rgba(87,204,153,0.60)",
  listening: "rgba(211,29,53,0.55)",
  thinking: "rgba(18,93,190,0.55)",
  executing: "rgba(6,131,43,0.70)",
};

const CORE_COLORS: Record<AgentOrbState, string> = {
  idle: "#57CC99",
  seeing: "#57CC99",
  listening: "#FF6B78",
  thinking: "#67BAEE",
  executing: "#06832B",
};

export const AgentOrb = ({ state = "idle", size = 28, className }: AgentOrbProps) => {
  const pulse = state === "seeing" || state === "listening";
  const ring = RING_COLORS[state];
  const core = CORE_COLORS[state];

  return (
    <div
      className={cn("relative flex items-center justify-center shrink-0", className)}
      style={{ width: size, height: size }}
    >
      {/* Pulsing ring for active states */}
      {pulse && (
        <span
          className="absolute inset-0 rounded-full animate-ping"
          style={{ background: ring, animationDuration: "1.4s" }}
        />
      )}

      {/* Static ring glow */}
      <span
        className="absolute inset-0 rounded-full"
        style={{ background: ring, opacity: 0.3 }}
      />

      {/* Core orb */}
      <span
        className="relative rounded-full"
        style={{
          width: size * 0.62,
          height: size * 0.62,
          background: `radial-gradient(circle at 30% 30%, white, ${core} 60%)`,
          boxShadow: `0 0 12px ${core}`,
        }}
      />
    </div>
  );
};
